import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api, label, money, timeOf, type Booking, type Order, type Report } from '../lib/api';
import { useStoreEvents } from '../lib/events';
import { StoreLinks } from '../components/StoreLinks';
import { useBase, useScope } from '../scope';

const CHANNEL: Record<string, string> = { pickup: 'Pickup', delivery: 'Delivery', dine_in: 'Dine-in' };

/** just enough of a dining table to build its QR link */
type TableLink = { tableId: string; name: string };

const num = (r: Report | null, key: string): number => {
  const v = r?.totals?.[key];
  return typeof v === 'number' ? v : 0;
};

/** The day at a glance: today's numbers, orders needing action, open
 * tabs, tonight's bookings. Live on the store event stream. */
export function Overview() {
  const { rid, sid, store } = useScope();
  const base = useBase();
  const [summary, setSummary] = useState<Report | null>(null);
  const [tips, setTips] = useState<Report | null>(null);
  const [needsAction, setNeedsAction] = useState<Order[] | null>(null);
  const [openTabs, setOpenTabs] = useState<Order[] | null>(null);
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [tables, setTables] = useState<TableLink[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: store?.timezone }).format(new Date());
      const [sum, tp, fresh, tabs, book] = await Promise.all([
        api.get<Report>(`${base}/reports/sales/summary`, { preset: 'today' }),
        api.get<Report>(`${base}/reports/tips`, { preset: 'today' }),
        api.get<Order[]>(`${base}/orders`, { status: 'new', limit: 20 }),
        api.get<Order[]>(`${base}/orders`, { status: 'in_progress', limit: 50 }),
        api.get<Booking[]>(`${base}/bookings`, { date: today }),
      ]);
      setSummary(sum);
      setTips(tp);
      setNeedsAction(fresh);
      setOpenTabs(tabs);
      setBookings(book);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
    }
  }, [base, store?.timezone]);

  // The table links are static per store, so they load once rather than on
  // the tick the live numbers ride.
  useEffect(() => {
    api.get<TableLink[]>(`${base}/tables`).then(setTables).catch(() => setTables([]));
  }, [base]);

  useEffect(() => {
    if (!store) return;
    void load();
    const t = setInterval(() => void load(), 15_000);
    return () => clearInterval(t);
  }, [load, store]);
  useStoreEvents(base, ['orders', 'bookings'], () => void load());

  async function accept(orderId: string) {
    try {
      await api.post(`${base}/orders/${orderId}/accept`, { etaMinutes: 15 });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Accept failed.');
    }
  }

  const today = new Date().toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: store?.timezone,
  });

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">{today} · {store?.name ?? '…'}</div>
          <h1>Overview</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <StoreLinks rid={rid} sid={sid} tables={tables} />

      <div className="stats" style={{ marginBottom: 28 }} data-section="stats">
        <div className="stat">
          <div className="overline">Today's revenue</div>
          <div className="v money" data-stat="revenue">{summary ? money(num(summary, 'revenue')) : '—'}</div>
          <div className="d">{summary && num(summary, 'discounts') > 0 ? `${money(num(summary, 'discounts'))} discounted` : ' '}</div>
        </div>
        <div className="stat">
          <div className="overline">Orders</div>
          <div className="v money" data-stat="orders">{summary ? num(summary, 'orders') : '—'}</div>
          <div className="d">{summary && num(summary, 'edited') > 0 ? `${num(summary, 'edited')} edited` : ' '}</div>
        </div>
        <div className="stat">
          <div className="overline">Avg order</div>
          <div className="v money" data-stat="average">{summary ? money(num(summary, 'average_order')) : '—'}</div>
          <div className="d">&nbsp;</div>
        </div>
        <div className="stat">
          <div className="overline">Tips</div>
          <div className="v money" data-stat="tips">{tips ? money(num(tips, 'tips')) : '—'}</div>
          <div className="d">&nbsp;</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div className="panel" data-section="needs-action">
            <h2>Needs action</h2>
            {needsAction === null && <div className="loading">Loading…</div>}
            {needsAction?.length === 0 && <div className="loading">No new orders waiting. 🎉</div>}
            {needsAction?.map((o) => (
              <div key={o.orderId} className="rowline">
                <span className={`src src-${o.source}`}>{label(o.source)}</span>
                <div className="grow">
                  <b>{o.customerName ?? 'Guest'} · {CHANNEL[o.collectionType] ?? o.collectionType}</b>
                  <span className="sub">
                    {timeOf(o.placedAt, store?.timezone)} · {o.lines.length} {o.lines.length === 1 ? 'line' : 'lines'} · {money(o.grandTotalInCents)}
                    {o.requestedFor ? ` · for ${timeOf(o.requestedFor, store?.timezone)}` : ''}
                  </span>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => void accept(o.orderId)}>
                  Accept · 15 min
                </button>
                <Link className="btn btn-outline btn-sm" to={`/r/${rid}/s/${sid}/orders/${o.orderId}`}>
                  View
                </Link>
              </div>
            ))}
          </div>

          <div className="panel" data-section="open-tabs">
            <h2>Open tabs (in progress)</h2>
            {openTabs === null && <div className="loading">Loading…</div>}
            {openTabs?.length === 0 && <div className="loading">No open POS tabs.</div>}
            {openTabs?.map((o) => (
              <Link key={o.orderId} className="rowline" style={{ textDecoration: 'none', color: 'inherit' }}
                to={`/r/${rid}/s/${sid}/orders/${o.orderId}`}>
                <span className="docket" style={{ fontSize: 18 }}>#{o.docketLabel ?? '—'}</span>
                <div className="grow">
                  <b>{o.dineIn ? `Table ${o.dineIn.tableName}` : CHANNEL[o.collectionType] ?? o.collectionType}
                    {o.dineIn?.guestCount ? ` · ${o.dineIn.guestCount} guests` : ''}</b>
                  <span className="sub">
                    {o.lines.filter((l) => !l.voidedAt).length} lines · {money(o.grandTotalInCents)} ·{' '}
                    <span className={`chip chip-${o.paymentState}`} style={{ fontSize: 12 }}>{label(o.paymentState)}</span>
                    {o.owner !== 'server' ? ' · on a terminal' : ''}
                  </span>
                </div>
                <span style={{ color: 'var(--ink-3)' }}>→</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="panel" data-section="bookings">
          <h2>Tonight's bookings</h2>
          {bookings === null && <div className="loading">Loading…</div>}
          {bookings?.length === 0 && <div className="loading">No bookings today.</div>}
          {bookings?.map((b) => (
            <div key={b.bookingId} className="rowline">
              <span className="tm">{b.bookingTime}</span>
              <div className="grow">
                <b>{b.customerName}</b>
                <span className="sub">
                  {b.pax} guests{b.tableName ? ` · ${b.tableName}` : ''}{b.notes ? ` · ${b.notes}` : ''}
                </span>
              </div>
              <span className={`chip chip-${b.status}`}>{b.status}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
